import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API from './apiConfig';
import { useModal } from './components/CustomModal';

const Tab = ({ active, label, count, onClick, testId }) => (
  <button onClick={onClick} data-testid={testId || `tab-${label.toLowerCase()}`}
    className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
      active ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' : 'text-zinc-400 hover:text-white hover:bg-zinc-800 border border-transparent'
    }`}>
    {label} {count != null && <span className="ml-1 opacity-60">({count})</span>}
  </button>
);

const SortHeader = ({ label, sortKey, sort, onSort, className = '' }) => {
  const active = sort.key === sortKey;
  return (
    <th className={`px-2 py-1.5 text-left cursor-pointer hover:text-blue-400 select-none ${className}`}
      onClick={() => onSort(sortKey)}>
      {label} {active && (sort.asc ? '\u25B2' : '\u25BC')}
    </th>
  );
};

const ConfidenceAmpel = ({ counts }) => {
  if (!counts) return <span className="text-zinc-700">–</span>;
  const { high = 0, medium = 0, low = 0, none = 0 } = counts;
  const total = high + medium + low + none;
  if (!total) return <span className="text-zinc-700">–</span>;
  const color = high >= medium + low + none ? 'bg-emerald-500'
    : low > 0 || none > high + medium ? 'bg-red-500' : 'bg-amber-500';
  const title = `Text-Abgleich: ${high} hoch · ${medium} mittel · ${low} niedrig · ${none} ohne (n=${total})`;
  return (
    <span title={title} className="inline-flex items-center gap-1 text-[10px] font-mono text-zinc-400">
      <span className={`inline-block w-2 h-2 rounded-full ${color}`} />{high}/{total}
    </span>
  );
};

export default function ElementLists({ analysisId, onHighlight, onClose }) {
  const modal = useModal();
  const [data, setData] = useState(null);          // aggregate buckets
  const [rawSvgData, setRawSvgData] = useState(null); // full element list (with ids)
  const [stats, setStats] = useState(null);        // /lv-stats
  const [manualPositions, setManualPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('walls');
  const [roomHeight, setRoomHeight] = useState(2.70);
  const [tolerance, setTolerance] = useState(25);
  const [sort, setSort] = useState({ key: 'thickness_mm', asc: true });
  const [selectedAggregate, setSelectedAggregate] = useState(null); // { kind, member_ids, label }
  const [pendingEdits, setPendingEdits] = useState({}); // { elementId: { field: value } }
  const [savingId, setSavingId] = useState(null);

  const reloadAll = useCallback(async () => {
    if (!analysisId) return;
    setLoading(true);
    try {
      const [agg, an, mp, ls] = await Promise.all([
        fetch(`${API}/api/analyses/${analysisId}/element-lists?room_height=${roomHeight}&tolerance=${tolerance}`).then(r => r.json()),
        axios.get(`${API}/api/analyses/${analysisId}`).then(r => r.data),
        axios.get(`${API}/api/analyses/${analysisId}/manual-positions`).then(r => r.data),
        axios.get(`${API}/api/analyses/${analysisId}/lv-stats?room_height=${roomHeight}`).then(r => r.data),
      ]);
      setData(agg);
      setRawSvgData(an.svg_data || an);
      setManualPositions(mp.positions || []);
      setStats(ls);
      setLoading(false);
    } catch (e) { setError(e.message); setLoading(false); }
  }, [analysisId, roomHeight, tolerance]);

  useEffect(() => { reloadAll(); }, [reloadAll]);

  const toggleSort = (key) => setSort(prev => prev.key === key ? { key, asc: !prev.asc } : { key, asc: true });
  const sorted = (arr, key) => {
    if (!arr) return [];
    const s = [...arr];
    s.sort((a, b) => {
      const va = a[key] ?? 0, vb = b[key] ?? 0;
      return sort.asc ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1);
    });
    return s;
  };

  const handleAggregateClick = (kind, agg) => {
    setSelectedAggregate({ kind, member_ids: agg.member_ids || [], label: agg.label });
    if (onHighlight) onHighlight(agg.member_ids || []);
  };

  const setEdit = (id, field, value) => {
    setPendingEdits(p => ({ ...p, [id]: { ...(p[id] || {}), [field]: value } }));
  };

  const saveWall = async (wallId) => {
    const edits = pendingEdits[wallId] || {};
    if (!Object.keys(edits).length) return;
    setSavingId(wallId);
    try {
      await axios.put(`${API}/api/analyses/${analysisId}/wall/${wallId}`, edits);
      setPendingEdits(p => { const n = { ...p }; delete n[wallId]; return n; });
      await reloadAll();
    } catch (e) { console.error(e); }
    setSavingId(null);
  };

  const saveOpening = async (openingId) => {
    const edits = pendingEdits[openingId] || {};
    if (!Object.keys(edits).length) return;
    setSavingId(openingId);
    try {
      await axios.put(`${API}/api/analyses/${analysisId}/opening/${openingId}`, edits);
      setPendingEdits(p => { const n = { ...p }; delete n[openingId]; return n; });
      await reloadAll();
    } catch (e) { console.error(e); }
    setSavingId(null);
  };

  const toggleHide = async (kind, id, current) => {
    const url = kind === 'wall'
      ? `${API}/api/analyses/${analysisId}/wall/${id}`
      : `${API}/api/analyses/${analysisId}/opening/${id}`;
    try {
      await axios.put(url, { excluded_from_lv: !current });
      await reloadAll();
    } catch (e) { console.error(e); }
  };

  if (loading && !data) return (
    <div className="h-full flex items-center justify-center text-zinc-500">
      <div className="animate-pulse">Klassifizierung wird berechnet…</div>
    </div>
  );
  if (error) return <div className="h-full flex items-center justify-center text-red-400">Fehler: {error}</div>;
  if (!data) return null;

  const { wall_list, window_list, door_list, room_list, summary } = data;

  return (
    <div className="h-full flex flex-col bg-zinc-950 text-zinc-200" data-testid="element-lists">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-zinc-800">
        <div className="flex items-center gap-3">
          <h2 className="text-sm font-bold tracking-wide text-white">LV-MASKE</h2>
          <div className="flex gap-1">
            <Tab active={tab === 'walls'} label="Wände" count={summary.wall_segments} onClick={() => { setTab('walls'); setSelectedAggregate(null); }} />
            <Tab active={tab === 'rooms'} label="Räume" count={summary.room_count} onClick={() => { setTab('rooms'); setSelectedAggregate(null); }} />
            <Tab active={tab === 'windows'} label="Fenster" count={summary.window_count} onClick={() => { setTab('windows'); setSelectedAggregate(null); }} />
            <Tab active={tab === 'doors'} label="Türen" count={summary.door_count} onClick={() => { setTab('doors'); setSelectedAggregate(null); }} />
            <Tab active={tab === 'manual'} label="Manuell" count={manualPositions.length} onClick={() => { setTab('manual'); setSelectedAggregate(null); }} testId="tab-manual" />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-[10px] text-zinc-500">
            <span>Raumhöhe</span>
            <input type="number" step="0.1" min="2" max="5" value={roomHeight}
              onChange={e => setRoomHeight(parseFloat(e.target.value) || 2.7)}
              className="w-14 bg-zinc-800 border border-zinc-700 rounded px-1.5 py-0.5 text-zinc-300 text-[10px]" />
            <span>m</span>
          </label>
          <button onClick={onClose} data-testid="close-lists-btn"
            className="text-zinc-500 hover:text-white text-lg px-1">×</button>
        </div>
      </div>

      {/* LV Statistics banner */}
      {stats && <StatsBanner stats={stats} />}

      {/* Body: split view */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left: aggregate list */}
        <div className="flex-1 overflow-auto px-4 py-2 border-r border-zinc-800">
          {tab === 'walls' && <WallTable walls={wall_list} sort={sort} onSort={toggleSort}
            sorted={sorted} onRowClick={(agg) => handleAggregateClick('wall', agg)}
            selectedLabel={selectedAggregate?.label} />}
          {tab === 'windows' && <WindowTable windows={window_list} sort={sort} onSort={toggleSort}
            sorted={sorted} onRowClick={(agg) => handleAggregateClick('window', agg)}
            selectedLabel={selectedAggregate?.label} />}
          {tab === 'doors' && <DoorTable doors={door_list} sort={sort} onSort={toggleSort}
            sorted={sorted} onRowClick={(agg) => handleAggregateClick('door', agg)}
            selectedLabel={selectedAggregate?.label} />}
          {tab === 'rooms' && <RoomTable rooms={room_list} sort={sort} onSort={toggleSort} sorted={sorted} />}
          {tab === 'manual' && <ManualPositionsTab positions={manualPositions}
            analysisId={analysisId} onChanged={reloadAll} />}
        </div>

        {/* Right: detail / member editor */}
        <div className="w-[420px] flex-shrink-0 overflow-auto bg-zinc-900/40" data-testid="lv-detail-panel">
          {!selectedAggregate || tab === 'rooms' || tab === 'manual' ? (
            <div className="h-full flex items-center justify-center text-zinc-600 text-xs px-6 text-center">
              {tab === 'rooms' ? 'Räume sind nicht editierbar — Werte folgen aus der Geometrie.'
                : tab === 'manual' ? 'Manuelle Positionen werden links bearbeitet.'
                : 'Element-Gruppe links auswählen, um die Einzelelemente hier zu bearbeiten.'}
            </div>
          ) : (
            <MemberEditor
              kind={selectedAggregate.kind}
              memberIds={selectedAggregate.member_ids}
              label={selectedAggregate.label}
              rawSvgData={rawSvgData}
              pendingEdits={pendingEdits}
              setEdit={setEdit}
              savingId={savingId}
              onSaveWall={saveWall}
              onSaveOpening={saveOpening}
              onToggleHide={toggleHide}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Stats banner (Grundfläche, AW/IW length+volume, Türen, Fenster)
// ──────────────────────────────────────────────────────────────────
function StatsBanner({ stats }) {
  const cell = (label, value, sub) => (
    <div className="flex flex-col">
      <span className="text-[9px] text-zinc-500 uppercase tracking-wider">{label}</span>
      <span className="font-mono text-sm text-zinc-100">{value}</span>
      {sub && <span className="text-[9px] text-zinc-500">{sub}</span>}
    </div>
  );
  return (
    <div className="grid grid-cols-7 gap-4 px-4 py-2 bg-gradient-to-b from-zinc-900/80 to-zinc-950 border-b border-zinc-800" data-testid="lv-stats-banner">
      {cell('Grundfläche', `${stats.totals.footprint_m2.toFixed(2)} m²`, `${stats.rooms.count} Räume`)}
      {cell('Außenwände', `${stats.exterior_walls.length_m.toFixed(2)} m`, `${stats.exterior_walls.count}× · ${stats.exterior_walls.volume_m3.toFixed(2)} m³`)}
      {cell('Innenwände', `${stats.interior_walls.length_m.toFixed(2)} m`, `${stats.interior_walls.count}× · ${stats.interior_walls.volume_m3.toFixed(2)} m³`)}
      {cell('Wände gesamt', `${stats.totals.wall_volume_m3.toFixed(2)} m³`, `${stats.totals.wall_area_m2.toFixed(0)} m² · ${stats.totals.wall_length_m.toFixed(0)} m`)}
      {cell('Türen', stats.doors.count, `${stats.doors.total_area_m2.toFixed(2)} m²`)}
      {cell('Fenster', stats.windows.count, `${stats.windows.total_area_m2.toFixed(2)} m²`)}
      {cell('Manuell', stats.manual_positions_count, 'aktiv')}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Right pane: edits the underlying individual members
// ──────────────────────────────────────────────────────────────────
function MemberEditor({ kind, memberIds, label, rawSvgData,
                       pendingEdits, setEdit, savingId,
                       onSaveWall, onSaveOpening, onToggleHide }) {
  if (!rawSvgData) return null;
  const collection = kind === 'wall' ? (rawSvgData.wall_lines || []) : (rawSvgData.openings || []);
  const members = collection.filter(el => memberIds.includes(el.id));

  if (!members.length) {
    return <div className="p-6 text-zinc-500 text-xs">Keine Einzelelemente zu dieser Gruppe gefunden.</div>;
  }

  return (
    <div className="p-3 space-y-2">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 px-1 pb-1 border-b border-zinc-800">
        {label} · {members.length} Element{members.length === 1 ? '' : 'e'}
      </div>
      {members.map(el => (
        kind === 'wall'
          ? <WallEditCard key={el.id} wall={el} edits={pendingEdits[el.id] || {}}
              setEdit={setEdit} saving={savingId === el.id}
              onSave={() => onSaveWall(el.id)} onToggleHide={() => onToggleHide('wall', el.id, !!el.excluded_from_lv)} />
          : <OpeningEditCard key={el.id} opening={el} edits={pendingEdits[el.id] || {}}
              setEdit={setEdit} saving={savingId === el.id}
              onSave={() => onSaveOpening(el.id)} onToggleHide={() => onToggleHide('opening', el.id, !!el.excluded_from_lv)} />
      ))}
    </div>
  );
}

const NumField = ({ label, value, onChange, unit, testId, step = '0.01' }) => (
  <label className="flex items-center justify-between gap-2 text-[11px]">
    <span className="text-zinc-500">{label}</span>
    <span className="flex items-center gap-1">
      <input type="number" step={step} value={value ?? ''}
        onChange={e => onChange(e.target.value === '' ? null : parseFloat(e.target.value))}
        data-testid={testId}
        className="w-20 bg-zinc-800 border border-zinc-700 rounded px-1.5 py-0.5 text-right text-zinc-100 font-mono" />
      <span className="text-zinc-500 text-[10px] w-6">{unit}</span>
    </span>
  </label>
);

function WallEditCard({ wall, edits, setEdit, saving, onSave, onToggleHide }) {
  const initialLength = wall.length_m_manual ?? wall.length_m_calibrated ?? wall.length_m_final ?? wall.length_m ?? null;
  const initialThicknessCm = (wall.thickness_m_manual ?? wall.thickness_m_final ?? wall.thickness_m ?? 0) * 100;
  const initialHeight = wall.height_m ?? null;
  const length = edits.length_m ?? initialLength;
  const widthCm = edits.width_cm ?? (initialThicknessCm || null);
  const height = edits.height_m ?? initialHeight;
  const dirty = Object.keys(edits).length > 0;
  const hidden = !!wall.excluded_from_lv;

  return (
    <div data-testid={`wall-edit-${wall.id}`}
      className={`rounded border px-3 py-2 ${hidden ? 'border-zinc-800 bg-zinc-900/40 opacity-60' : 'border-zinc-700 bg-zinc-900/70'}`}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="font-mono text-xs font-semibold text-zinc-100">{wall.name || wall.id}</div>
        <button onClick={onToggleHide} data-testid={`wall-hide-${wall.id}`}
          className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${hidden ? 'bg-emerald-900/40 text-emerald-300' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'}`}>
          {hidden ? '↺ einblenden' : '⊘ ausblenden'}
        </button>
      </div>
      <div className="space-y-1">
        <NumField label="Länge" value={length} unit="m" testId={`wall-length-${wall.id}`}
          onChange={v => setEdit(wall.id, 'length_m', v)} />
        <NumField label="Wandstärke" value={widthCm} unit="cm" step="0.1" testId={`wall-width-${wall.id}`}
          onChange={v => setEdit(wall.id, 'width_cm', v)} />
        <NumField label="Höhe" value={height} unit="m" testId={`wall-height-${wall.id}`}
          onChange={v => setEdit(wall.id, 'height_m', v)} />
      </div>
      <div className="mt-2 flex items-center gap-2 text-[9px] text-zinc-500">
        <span>{wall.wall_type === 'exterior' ? 'AW' : 'IW'}</span>
        <span>·</span>
        <span>Quelle: {wall.thickness_source || '–'}</span>
        <div className="flex-1" />
        {dirty && (
          <button onClick={onSave} disabled={saving} data-testid={`wall-save-${wall.id}`}
            className="px-2 py-0.5 rounded bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-700 text-white text-[10px] font-semibold">
            {saving ? '…' : 'Speichern'}
          </button>
        )}
      </div>
    </div>
  );
}

function OpeningEditCard({ opening, edits, setEdit, saving, onSave, onToggleHide }) {
  const initialW = opening.width_m_matched ?? opening.width_m ?? null;
  const initialH = opening.height_m_matched ?? opening.height_m ?? null;
  const w = edits.width_m ?? initialW;
  const h = edits.height_m ?? initialH;
  const dirty = Object.keys(edits).length > 0;
  const hidden = !!opening.excluded_from_lv;
  const isDoor = opening.type === 'door';

  return (
    <div data-testid={`opening-edit-${opening.id}`}
      className={`rounded border px-3 py-2 ${hidden ? 'border-zinc-800 bg-zinc-900/40 opacity-60' : 'border-zinc-700 bg-zinc-900/70'}`}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="font-mono text-xs font-semibold text-zinc-100">
          {opening.name || opening.id}
          <span className="ml-2 text-[9px] text-zinc-500">{isDoor ? 'Tür' : 'Fenster'}</span>
        </div>
        <button onClick={onToggleHide} data-testid={`opening-hide-${opening.id}`}
          className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${hidden ? 'bg-emerald-900/40 text-emerald-300' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'}`}>
          {hidden ? '↺ einblenden' : '⊘ ausblenden'}
        </button>
      </div>
      <div className="space-y-1">
        <NumField label="Breite" value={w} unit="m" testId={`opening-width-${opening.id}`}
          onChange={v => setEdit(opening.id, 'width_m', v)} />
        <NumField label="Höhe" value={h} unit="m" testId={`opening-height-${opening.id}`}
          onChange={v => setEdit(opening.id, 'height_m', v)} />
        {isDoor && (
          <>
            <label className="flex items-center justify-between gap-2 text-[11px]">
              <span className="text-zinc-500">Anschlag</span>
              <select value={edits.opening_direction ?? opening.opening_direction ?? ''}
                onChange={e => setEdit(opening.id, 'opening_direction', e.target.value || null)}
                data-testid={`opening-direction-${opening.id}`}
                className="bg-zinc-800 border border-zinc-700 rounded px-1.5 py-0.5 text-zinc-100 text-[11px] w-28">
                <option value="">–</option>
                <option value="links">DIN-L (links)</option>
                <option value="rechts">DIN-R (rechts)</option>
              </select>
            </label>
            <label className="flex items-center justify-between gap-2 text-[11px]">
              <span className="text-zinc-500">Öffnet in</span>
              <input type="text" value={edits.swing_room ?? opening.swing_room ?? ''}
                onChange={e => setEdit(opening.id, 'swing_room', e.target.value)}
                data-testid={`opening-swing-${opening.id}`}
                placeholder="Raumname / Innen / Außen"
                className="w-32 bg-zinc-800 border border-zinc-700 rounded px-1.5 py-0.5 text-zinc-100 text-[11px]" />
            </label>
          </>
        )}
      </div>
      <div className="mt-2 flex items-center gap-2 text-[9px] text-zinc-500">
        <span>Quelle: {opening.width_source || '–'}</span>
        <div className="flex-1" />
        {dirty && (
          <button onClick={onSave} disabled={saving} data-testid={`opening-save-${opening.id}`}
            className="px-2 py-0.5 rounded bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-700 text-white text-[10px] font-semibold">
            {saving ? '…' : 'Speichern'}
          </button>
        )}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Manual positions tab
// ──────────────────────────────────────────────────────────────────
// ─── Manuelle Positionen Tab ───────────────────────────────────────
// HINWEIS: Eingabefelder MÜSSEN außerhalb der Parent-Komponente definiert werden,
// sonst wird das input-DOM bei jedem Tastendruck neu erzeugt → Focus-Verlust.
const ManualField = React.memo(function ManualField({ label, k, unit, type = 'text', value, onChange }) {
  return (
    <label className="flex flex-col text-[10px] text-zinc-500">
      <span>{label}</span>
      <input type={type} step="0.01" value={value ?? ''}
        onChange={e => onChange(k, type === 'number' ? (e.target.value === '' ? null : parseFloat(e.target.value)) : e.target.value)}
        data-testid={`manual-input-${k}`}
        placeholder={unit || ''}
        className="bg-zinc-800 border border-zinc-700 rounded px-1.5 py-1 text-zinc-100 text-xs" />
    </label>
  );
});

function ManualPositionsTab({ positions, analysisId, onChanged }) {
  const [draft, setDraft] = useState({});
  const [adding, setAdding] = useState(false);

  const setField = useCallback((k, v) => {
    setDraft(d => ({ ...d, [k]: v }));
  }, []);

  const submitDraft = async () => {
    if (!draft.title) return;
    setAdding(true);
    try {
      await axios.post(`${API}/api/analyses/${analysisId}/manual-positions`, draft);
      setDraft({});
      onChanged();
    } catch (e) { console.error(e); }
    setAdding(false);
  };

  const updatePos = async (id, patch) => {
    try {
      await axios.put(`${API}/api/analyses/${analysisId}/manual-positions/${id}`, patch);
      onChanged();
    } catch (e) { console.error(e); }
  };

  const deletePos = async (id) => {
    if (!await modal.confirm('Position löschen?', { title: 'Löschen', confirmText: 'Löschen', type: 'warning' })) return;
    try {
      await axios.delete(`${API}/api/analyses/${analysisId}/manual-positions/${id}`);
      onChanged();
    } catch (e) { console.error(e); }
  };

  return (
    <div className="space-y-3" data-testid="manual-positions-tab">
      {/* Add new */}
      <div className="border border-zinc-700 rounded p-3 bg-zinc-900/40">
        <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Neue Position hinzufügen</div>
        <div className="grid grid-cols-3 gap-2">
          <div className="col-span-2">
            <ManualField label="Artikel / Titel *" k="title" value={draft.title} onChange={setField} />
          </div>
          <ManualField label="Kategorie" k="category" value={draft.category} onChange={setField} />
          <ManualField label="Menge" k="quantity" type="number" value={draft.quantity} onChange={setField} />
          <ManualField label="Einheit" k="unit" unit="Stk / m / m² / m³" value={draft.unit} onChange={setField} />
          <ManualField label="Einzelpreis" k="unit_price_eur" type="number" unit="€" value={draft.unit_price_eur} onChange={setField} />
          <ManualField label="Länge" k="length_m" type="number" unit="m" value={draft.length_m} onChange={setField} />
          <ManualField label="Fläche" k="area_m2" type="number" unit="m²" value={draft.area_m2} onChange={setField} />
          <ManualField label="Volumen" k="volume_m3" type="number" unit="m³" value={draft.volume_m3} onChange={setField} />
        </div>
        <div className="mt-2">
          <label className="flex flex-col text-[10px] text-zinc-500">
            <span>Beschreibung</span>
            <textarea value={draft.description ?? ''}
              onChange={e => setField('description', e.target.value)}
              data-testid="manual-input-description"
              className="bg-zinc-800 border border-zinc-700 rounded px-1.5 py-1 text-zinc-100 text-xs" rows={2} />
          </label>
        </div>
        <div className="mt-2 flex justify-end">
          <button onClick={submitDraft} disabled={adding || !draft.title}
            data-testid="manual-add-btn"
            className="px-3 py-1 rounded bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-700 text-white text-xs font-semibold">
            {adding ? 'Speichere…' : '+ Position hinzufügen'}
          </button>
        </div>
      </div>

      {/* Existing positions */}
      {positions.length === 0 ? (
        <div className="text-zinc-600 text-xs text-center py-6">Noch keine manuellen Positionen.</div>
      ) : (
        <table className="w-full text-xs" data-testid="manual-positions-table">
          <thead className="text-zinc-500 text-[10px] uppercase tracking-wider border-b border-zinc-800">
            <tr>
              <th className="px-2 py-1.5 text-left">Titel</th>
              <th className="px-2 py-1.5 text-left">Kategorie</th>
              <th className="px-2 py-1.5 text-right">Menge</th>
              <th className="px-2 py-1.5 text-left">Einheit</th>
              <th className="px-2 py-1.5 text-right">EP</th>
              <th className="px-2 py-1.5 text-right">Summe</th>
              <th className="px-2 py-1.5 text-center">Aktion</th>
            </tr>
          </thead>
          <tbody>
            {positions.map(p => {
              const total = (p.quantity || 0) * (p.unit_price_eur || 0);
              return (
                <tr key={p.id} data-testid={`manual-row-${p.id}`}
                  className={`border-b border-zinc-800/50 ${p.excluded_from_lv ? 'opacity-50 line-through' : 'hover:bg-zinc-800/30'}`}>
                  <td className="px-2 py-1.5">
                    <div className="text-zinc-100 font-medium">{p.title || '–'}</div>
                    {p.description && <div className="text-[10px] text-zinc-500">{p.description}</div>}
                  </td>
                  <td className="px-2 py-1.5 text-zinc-400">{p.category || '–'}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{p.quantity ?? '–'}</td>
                  <td className="px-2 py-1.5 text-zinc-400">{p.unit || '–'}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{p.unit_price_eur != null ? `${p.unit_price_eur.toFixed(2)} €` : '–'}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-zinc-200">{total ? `${total.toFixed(2)} €` : '–'}</td>
                  <td className="px-2 py-1.5 text-center">
                    <button onClick={() => updatePos(p.id, { excluded_from_lv: !p.excluded_from_lv })}
                      data-testid={`manual-hide-${p.id}`}
                      className={`text-[10px] px-1.5 py-0.5 rounded mr-1 ${p.excluded_from_lv ? 'bg-emerald-900/40 text-emerald-300' : 'bg-zinc-800 text-zinc-400'}`}>
                      {p.excluded_from_lv ? '↺' : '⊘'}
                    </button>
                    <button onClick={() => deletePos(p.id)} data-testid={`manual-del-${p.id}`}
                      className="text-[10px] px-1.5 py-0.5 rounded bg-red-900/40 text-red-300 hover:bg-red-900/60">
                      🗑
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Aggregate tables (kept from previous version, simplified click)
// ──────────────────────────────────────────────────────────────────
function WallTable({ walls, sort, onSort, sorted, onRowClick, selectedLabel }) {
  const rows = sorted(walls, sort.key);
  return (
    <table className="w-full text-xs" data-testid="wall-table">
      <thead className="text-zinc-500 text-[10px] uppercase tracking-wider border-b border-zinc-800">
        <tr>
          <SortHeader label="Typ" sortKey="label" sort={sort} onSort={onSort} />
          <SortHeader label="Dicke" sortKey="thickness_mm" sort={sort} onSort={onSort} className="text-right" />
          <SortHeader label="Anz" sortKey="count" sort={sort} onSort={onSort} className="text-right" />
          <SortHeader label="Ges.Länge" sortKey="total_length_m" sort={sort} onSort={onSort} className="text-right" />
          <SortHeader label="Fläche" sortKey="total_area_m2" sort={sort} onSort={onSort} className="text-right" />
          <SortHeader label="Volumen" sortKey="total_volume_m3" sort={sort} onSort={onSort} className="text-right" />
          <th className="px-2 py-1.5 text-center">Konf.</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((w, i) => (
          <tr key={i} onClick={() => onRowClick(w)}
            data-testid={`wall-row-${w.thickness_mm}-${w.wall_type}`}
            className={`border-b border-zinc-800/30 cursor-pointer transition-colors ${
              selectedLabel === w.label ? 'bg-blue-900/30 text-blue-200' :
              !w.matched ? 'bg-amber-900/10 text-amber-300' :
              w.wall_type === 'exterior' ? 'hover:bg-zinc-800/50' : 'hover:bg-zinc-800/30 text-zinc-400'
            }`}>
            <td className="px-2 py-1.5 font-medium whitespace-nowrap">
              <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 ${w.wall_type === 'exterior' ? 'bg-red-400' : 'bg-blue-400'}`} />
              {w.label}{!w.matched && <span className="ml-1 text-[9px] text-amber-500">OUTLIER</span>}
            </td>
            <td className="px-2 py-1.5 text-right font-mono">{w.thickness_mm} mm</td>
            <td className="px-2 py-1.5 text-right">{w.count}</td>
            <td className="px-2 py-1.5 text-right font-mono">{w.total_length_m.toFixed(2)} m</td>
            <td className="px-2 py-1.5 text-right font-mono">{w.total_area_m2.toFixed(1)} m²</td>
            <td className="px-2 py-1.5 text-right font-mono">{w.total_volume_m3.toFixed(2)} m³</td>
            <td className="px-2 py-1.5 text-center"><ConfidenceAmpel counts={w.confidence_counts} /></td>
          </tr>
        ))}
      </tbody>
      <tfoot className="border-t border-zinc-700 text-zinc-300 font-medium">
        <tr>
          <td className="px-2 py-1.5">Gesamt</td>
          <td />
          <td className="px-2 py-1.5 text-right">{walls.reduce((s, w) => s + w.count, 0)}</td>
          <td className="px-2 py-1.5 text-right font-mono">{walls.reduce((s, w) => s + w.total_length_m, 0).toFixed(2)} m</td>
          <td className="px-2 py-1.5 text-right font-mono">{walls.reduce((s, w) => s + w.total_area_m2, 0).toFixed(1)} m²</td>
          <td className="px-2 py-1.5 text-right font-mono">{walls.reduce((s, w) => s + w.total_volume_m3, 0).toFixed(2)} m³</td>
          <td />
        </tr>
      </tfoot>
    </table>
  );
}

function WindowTable({ windows, sort, onSort, sorted, onRowClick, selectedLabel }) {
  const rows = sorted(windows, sort.key);
  return (
    <table className="w-full text-xs" data-testid="window-table">
      <thead className="text-zinc-500 text-[10px] uppercase tracking-wider border-b border-zinc-800">
        <tr>
          <SortHeader label="Typ" sortKey="label" sort={sort} onSort={onSort} />
          <SortHeader label="Breite" sortKey="width_mm" sort={sort} onSort={onSort} className="text-right" />
          <SortHeader label="Höhe" sortKey="height_mm" sort={sort} onSort={onSort} className="text-right" />
          <SortHeader label="Anz" sortKey="count" sort={sort} onSort={onSort} className="text-right" />
        </tr>
      </thead>
      <tbody>
        {rows.map((w, i) => (
          <tr key={i} onClick={() => onRowClick(w)}
            data-testid={`window-row-${w.width_mm}`}
            className={`border-b border-zinc-800/30 cursor-pointer transition-colors ${
              selectedLabel === w.label ? 'bg-blue-900/30 text-blue-200' :
              !w.matched ? 'bg-amber-900/10 text-amber-300' : 'hover:bg-zinc-800/50'
            }`}>
            <td className="px-2 py-1.5 font-medium whitespace-nowrap">{w.label}{!w.matched && <span className="ml-1 text-[9px] text-amber-500">OUTLIER</span>}</td>
            <td className="px-2 py-1.5 text-right font-mono">{w.width_mm} mm</td>
            <td className="px-2 py-1.5 text-right font-mono">{w.height_mm ? `${w.height_mm} mm` : '-'}</td>
            <td className="px-2 py-1.5 text-right">{w.count}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function DoorTable({ doors, sort, onSort, sorted, onRowClick, selectedLabel }) {
  const rows = sorted(doors, sort.key);
  return (
    <table className="w-full text-xs" data-testid="door-table">
      <thead className="text-zinc-500 text-[10px] uppercase tracking-wider border-b border-zinc-800">
        <tr>
          <SortHeader label="Typ" sortKey="label" sort={sort} onSort={onSort} />
          <SortHeader label="Breite" sortKey="width_mm" sort={sort} onSort={onSort} className="text-right" />
          <SortHeader label="Höhe" sortKey="height_mm" sort={sort} onSort={onSort} className="text-right" />
          <SortHeader label="Anz" sortKey="count" sort={sort} onSort={onSort} className="text-right" />
          <th className="px-2 py-1.5 text-left">Blatt</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((d, i) => (
          <tr key={i} onClick={() => onRowClick(d)}
            data-testid={`door-row-${d.width_mm}`}
            className={`border-b border-zinc-800/30 cursor-pointer transition-colors ${
              selectedLabel === d.label ? 'bg-blue-900/30 text-blue-200' :
              !d.matched ? 'bg-amber-900/10 text-amber-300' : 'hover:bg-zinc-800/50'
            }`}>
            <td className="px-2 py-1.5 font-medium whitespace-nowrap">{d.label}{!d.matched && <span className="ml-1 text-[9px] text-amber-500">OUTLIER</span>}</td>
            <td className="px-2 py-1.5 text-right font-mono">{d.width_mm} mm</td>
            <td className="px-2 py-1.5 text-right font-mono">{d.height_mm ? `${d.height_mm} mm` : '-'}</td>
            <td className="px-2 py-1.5 text-right">{d.count}</td>
            <td className="px-2 py-1.5">{d.is_double_leaf ? '2F' : '1F'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function RoomTable({ rooms, sort, onSort, sorted }) {
  const rows = sorted(rooms, sort.key);
  return (
    <table className="w-full text-xs" data-testid="room-table">
      <thead className="text-zinc-500 text-[10px] uppercase tracking-wider border-b border-zinc-800">
        <tr>
          <SortHeader label="" sortKey="letter" sort={sort} onSort={onSort} className="w-6" />
          <SortHeader label="Raum" sortKey="name" sort={sort} onSort={onSort} />
          <SortHeader label="Fläche" sortKey="area_m2" sort={sort} onSort={onSort} className="text-right" />
          <SortHeader label="Umfang" sortKey="perimeter_m" sort={sort} onSort={onSort} className="text-right" />
          <SortHeader label="Boden" sortKey="floor_area_m2" sort={sort} onSort={onSort} className="text-right" />
          <SortHeader label="Decke" sortKey="ceiling_area_m2" sort={sort} onSort={onSort} className="text-right" />
          <SortHeader label="Wandfl." sortKey="wall_area_m2" sort={sort} onSort={onSort} className="text-right" />
          <SortHeader label="Volumen" sortKey="volume_m3" sort={sort} onSort={onSort} className="text-right" />
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-b border-zinc-800/30 hover:bg-zinc-800/30" data-testid={`room-row-${r.letter}`}>
            <td className="px-2 py-1.5 font-bold text-zinc-300">{r.letter}</td>
            <td className="px-2 py-1.5">{r.name}</td>
            <td className="px-2 py-1.5 text-right font-mono">{r.area_m2.toFixed(2)} m²</td>
            <td className="px-2 py-1.5 text-right font-mono">{r.perimeter_m.toFixed(2)} m</td>
            <td className="px-2 py-1.5 text-right font-mono">{r.floor_area_m2.toFixed(2)} m²</td>
            <td className="px-2 py-1.5 text-right font-mono">{r.ceiling_area_m2.toFixed(2)} m²</td>
            <td className="px-2 py-1.5 text-right font-mono">{r.wall_area_m2.toFixed(1)} m²</td>
            <td className="px-2 py-1.5 text-right font-mono">{r.volume_m3.toFixed(1)} m³</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
